import { forwardRef, type ForwardedRef } from 'react';
import { cx } from '@linaria/atomic';
import { splitStylesProp, type StylesProp } from '../../../helpers/styles-prop';
import { useRefInterceptor } from '../../../hooks/use-ref-interceptor';
import type { InputProps } from '../atoms/types';
import { ClearFieldBtn } from './clear-field-btn.component';
import { searchClearFieldBtn } from './field-layout.styles';
import { FieldWrapper } from './field-wrapper.component';
import { FloatingLabel } from './floating-label.component';
import { HelperText } from './helper-text/helper-text.component';
import { NonFloatingLabel } from './non-floating-label.component';
import type { FieldKey, FormFieldProps } from './types';
import { getFormFieldAtomProps } from './utils';

const FieldLayoutInner = (
  {
    outerProps,
    children,
    className,
    clearable,
    onClear,
    containerCss,
    endAdornment,
    field,
    fieldComponentProps,
    hasError,
    helperText,
    hidden,
    label,
    hasNonFloatingLabel,
    startAdornment,
    hideErrorText,
    hasPadding = true,
    /** Required when adding something to the start of a field */
    startAdornmentWidth = 16,
    onSearchSubmit,
    style,
    ...props
  }: FormFieldProps<FieldKey> & {
    hasPadding?: boolean;
    hasError?: boolean;
    style?: StylesProp;
  },
  ref?: ForwardedRef<HTMLDivElement>
) => {
  const [localRef, elementRef] = useRefInterceptor(ref);
  if (hidden) return null;
  // Ignore specialized props from custom value setters
  // TODO: This can be removed when we consolidate field configs
  const { ...rest } = props;

  const { fieldProps, descriptorProps, labelProps, wrapperProps } = getFormFieldAtomProps({
    ...(rest as any),
    descriptorText: helperText,
    label,
  });
  const FieldComponent = field;

  const clearField = () => {
    fieldProps.onChange({ name: rest.name, value: '' });
    onClear?.();
    if (localRef) {
      const input = localRef.current?.querySelector('input');
      input?.focus();
    }
  };
  const { active, ...restLabelProps } = labelProps;
  const containerStyles = splitStylesProp(containerCss);
  const containerClassName =
    containerStyles.className && outerProps?.className
      ? cx(containerStyles.className, outerProps.className)
      : containerStyles.className ?? outerProps?.className;

  return (
    <div
      {...(containerStyles.css !== undefined ? { css: containerStyles.css } : undefined)}
      ref={elementRef}
      className={containerClassName}
      {...outerProps}
    >
      {!!label && hasNonFloatingLabel && <NonFloatingLabel {...restLabelProps}>{label}</NonFloatingLabel>}
      <FieldWrapper className={className} hasPadding={hasPadding} style={style} {...wrapperProps}>
        {!!label && !hasNonFloatingLabel && (
          <FloatingLabel
            {...restLabelProps}
            active={active}
            leftOffset={startAdornment ? startAdornmentWidth : undefined}
            hasError={wrapperProps.hasError}
          >
            {label}
          </FloatingLabel>
        )}
        {!onSearchSubmit && startAdornment}
        <FieldComponent {...(fieldProps as InputProps<any>)} {...(fieldComponentProps ? fieldComponentProps : {})} />
        {!onSearchSubmit && endAdornment}
        {!onSearchSubmit && clearable && !endAdornment && !!rest.value && (
          <ClearFieldBtn onClick={clearField} onBlur={() => rest?.onBlur()} onFocus={() => rest?.onFocus()} />
        )}
        {onSearchSubmit && clearable && !!rest.value && (
          <ClearFieldBtn
            className={searchClearFieldBtn}
            onClick={clearField}
            onBlur={() => rest?.onBlur()}
            onFocus={() => rest?.onFocus()}
          />
        )}
        {onSearchSubmit && endAdornment}
        {children}
      </FieldWrapper>
      {!hideErrorText && !!descriptorProps.children && <HelperText {...descriptorProps} />}
    </div>
  );
};

export const FieldLayout = forwardRef(FieldLayoutInner);
